import { getAccessToken } from '@opengov/app-core/auth/accessTokenForNonReactContextsStore';
import { TableFormatter, tableFormatter } from '@opengov/reporting-formatters';
import {
  CALCULATION_COLUMN_GROUP_HEADER,
  ColumnValue,
  ColumnValueSegment,
  ELedgerTypeDisplay,
  getColumnValueMapping,
  IAccountSummary,
  IColumnAggregation,
  IColumnDataSource,
  IColumnGroupColumn,
  IColumnSource,
  IGroupExpression,
  IReportDatasetLens,
  isCalculationExpression,
  isColumnDataSource,
  ISingleValueColumnFilter,
  isLedgerType,
  isMultiHierarchyCalculatedColumnGroup,
  isMultiHierarchyColumnDataSource,
  IStringConstantExpression,
  MultiHierarchyAccountSummary,
  MultiHierarchyColumnDataSource,
  RelativeFilter,
  TOTALS_COLUMN_VALUE_KEY,
  buildColumnPathKey,
  columnValueLevelCount,
  labelSlot
} from '@opengov/reporting-types';
import { ColDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { groupBy, uniqueId } from 'lodash';
import { getLensesForReport } from '../../api/lensStore';
import * as api from '../../api/reportingApi';
import { ILocaleFormatter, IValueFormatter } from '../../locale/types';
import { brokenCalcColumnTooltip } from '../editor/sidepanel/Calculations/brokenCalcColumnText';
import { findBrokenCalcColumns, type BrokenCalcColumn } from '../editor/sidepanel/Calculations/findBrokenCalcColumns';
import { AG_GRID_MIN_COLUMN_WIDTH } from './agGridConsts';
import { buildColumnHeaderTree, columnPathIdsOf, type ColumnHeaderNode } from './buildColumnHeaderTree';
import { AG_MDS_COLUMN_HEADER_CLASS, CALCULATION_CELL_CLASS } from './constants';
import { QueryContext } from './getRowData';
import { areAllColumnGroupHeadersHidden } from './gridUtils';
import { isTruthy } from './utils';

/**
 * The first candidate that is a non-empty string.
 *
 * Deliberately not `??`: a rename can be absent OR empty, and an empty rename must fall through to
 * the real label rather than blanking the header, which `??` would not do. Naming the intent also
 * keeps the fallback chains readable where three candidates line up.
 */
const firstNonEmpty = (...candidates: Array<string | null | undefined>): string =>
  candidates.find((candidate) => Boolean(candidate)) ?? '';

export const hasColumnValueMapping = (
  columnSource: IColumnDataSource | MultiHierarchyColumnDataSource,
  value: ColumnValueSegment | ColumnValueSegment[],
  level = 1
): boolean => {
  // A path's identity is its deepest segment plus its ancestors, so an absent leaf means there is
  // nothing to have renamed — same rule the single-value form has always applied.
  const leafSegment = Array.isArray(value) ? value[value.length - 1] : value;
  if (!leafSegment) return false;
  return !!getColumnValueMapping(columnSource, value, level);
};

export const transformAccountTypeColumnName = (val: ColumnValueSegment) => {
  if (val !== null && typeof val === 'string' && isLedgerType(val)) {
    return ELedgerTypeDisplay[val];
  }
  return val;
};

const valueColumn = ({
  colDef,
  columnSource,
  formatter,
  valueGetterIndex,
  value,
  formattedValue,
  columnPath,
  level
}: {
  colDef: ColDef;
  columnSource: IColumnSource;
  formatter: IValueFormatter;
  valueGetterIndex: number;
  column?: ColumnValueSegment;
  lens?: IReportDatasetLens;
  value: string;
  formattedValue: string;
  /**
   * The column's full level path and depth, for a column under a multi-level organize-by. Omitted
   * for a single-level column, which keeps looking its rename up by bare value at level 1.
   *
   * This has to be threaded through rather than derived from `value`: `headerValueGetter` wins over
   * `headerName` in AG Grid, so resolving the rename by leaf value here would quietly override a
   * correctly-resolved header and let two equal leaf labels read each other's rename.
   */
  columnPath?: ColumnValueSegment[];
  level?: number;
}): ColDef & { isValueColumn: boolean; display: string; hasColumnValueMapping: boolean } => {
  const renameTarget = columnPath ?? value;
  const renameLevel = level ?? 1;
  return {
    isValueColumn: true,
    valueFormatter: formatter,
    headerValueGetter:
      isColumnDataSource(columnSource) || isMultiHierarchyColumnDataSource(columnSource)
        ? () => firstNonEmpty(getColumnValueMapping(columnSource, renameTarget, renameLevel), formattedValue, value)
        : () => firstNonEmpty(formattedValue, value),
    headerClass: 'mds-grid-header',
    valueGetter: (params: ValueGetterParams) => {
      return params.data?.values[valueGetterIndex];
    },
    // A parent row whose response is still in flight shows a loading block instead of a blank
    // value; undefined falls back to AG Grid's default text rendering.
    cellRendererSelector: (params: ICellRendererParams<{ pendingValues?: boolean }>) =>
      params.data?.pendingValues ? { component: 'PendingValueCell' } : undefined,
    type: 'numericColumn',
    display: (columnSource as IGroupExpression).display ?? 'default',
    hasColumnValueMapping:
      (isColumnDataSource(columnSource) || isMultiHierarchyColumnDataSource(columnSource)) &&
      hasColumnValueMapping(columnSource, renameTarget, renameLevel),
    ...colDef
  };
};

interface CreatePivotColArgsForSingleHeader {
  emptyPivotColHeader: string;
  formatter: IValueFormatter;
  valueGetterIndex: number;
  segments: number;
  source: IColumnDataSource | IColumnAggregation | MultiHierarchyColumnDataSource;
  parentId: string;
  value: string;
  formattedValue: string;
  /** The column's full level path and depth. A one-level column passes a single-segment path. */
  columnPath: ColumnValueSegment[];
  level: number;
}

/**
 * One flat value column, for the hidden-column-group-headers layout.
 *
 * Keyed on the FULL level path like the grouped renderer: with headers hidden there is nothing to
 * nest, but two deep columns sharing a level-1 id would otherwise be emitted with the same field —
 * one AG Grid column key for two columns of data.
 */
const createPivotColForSingleHeader = ({
  emptyPivotColHeader,
  formatter,
  valueGetterIndex,
  segments,
  parentId,
  source,
  value,
  formattedValue,
  columnPath,
  level
}: CreatePivotColArgsForSingleHeader): ColDef => {
  const headerName = firstNonEmpty(
    getColumnValueMapping(source, columnPath, level),
    formattedValue,
    emptyPivotColHeader
  );
  const pathKey = buildColumnPathKey(columnPath);
  // Totals keeps the field the flat renderer gave it — AG Grid derives colId from field, and saved
  // column order is stored per colId.
  const fieldValue =
    pathKey === TOTALS_COLUMN_VALUE_KEY ? emptyPivotColHeader : firstNonEmpty(pathKey, emptyPivotColHeader);

  return valueColumn({
    colDef: {
      field: `${parentId}.${fieldValue}.`,
      headerName,
      minWidth: AG_GRID_MIN_COLUMN_WIDTH / segments
    },
    columnSource: source,
    formatter,
    valueGetterIndex,
    value,
    formattedValue,
    columnPath,
    level
  });
};

/** A rendered header: AG Grid's own ColDef plus the extras this module attaches to value columns. */
type HeaderColDef = ColDef & { children?: HeaderColDef[]; isFormattedColumnName?: boolean };

interface CreateHeaderColDefArgs {
  node: ColumnHeaderNode;
  source: IColumnDataSource | MultiHierarchyColumnDataSource;
  parentId: string;
  emptyPivotColHeader: string;
  /** Divides the minimum width across the column group's leaves, as the flat renderer did. */
  leafCount: number;
  formatterFor: (node: ColumnHeaderNode) => IValueFormatter;
  valueGetterIndexFor: (node: ColumnHeaderNode) => number;
  /** The response tuple a leaf came from — consumers of the click payload read it positionally. */
  tupleFor: (node: ColumnHeaderNode) => ColumnValue | undefined;
  onLeafClick: (node: ColumnHeaderNode, tuple: ColumnValue | undefined) => void;
}

/**
 * Renders one header node. A node with children becomes an AG Grid header group; a leaf becomes a
 * value column whose field is keyed on the FULL level path, so two equal leaf labels under different
 * parents cannot collide on one field and display each other's data.
 *
 * Every node resolves its own rename against its own partial path, which is why a rename set at
 * level 2 lands on the level-2 header rather than on its parent or its children.
 */
const createHeaderColDef = (args: CreateHeaderColDefArgs): HeaderColDef => {
  const { node, source, parentId, emptyPivotColHeader, leafCount } = args;
  const renamed = getColumnValueMapping(source, node.ids, node.level);
  const headerName = firstNonEmpty(renamed, node.label, emptyPivotColHeader);

  if (node.children.length > 0) {
    return {
      field: 'column_grouping',
      headerName,
      children: node.children.map((child) => createHeaderColDef({ ...args, node: child }))
    };
  }

  const leafValue = node.ids[node.ids.length - 1];
  // The totals sentinel keeps the field the flat renderer gave it. AG Grid derives a colId from the
  // field, and saved column order is stored per colId, so changing it would silently drop the saved
  // position of every existing totals column.
  const fieldKey = node.pathKey === TOTALS_COLUMN_VALUE_KEY ? emptyPivotColHeader : node.pathKey;

  return {
    ...valueColumn({
      colDef: {
        field: `${parentId}.${fieldKey}.`,
        headerName,
        minWidth: AG_GRID_MIN_COLUMN_WIDTH / Math.max(leafCount, 1)
      },
      columnSource: source,
      formatter: args.formatterFor(node),
      valueGetterIndex: args.valueGetterIndexFor(node),
      value: String(leafValue),
      formattedValue: node.label,
      columnPath: node.ids,
      level: node.level
    }),
    isFormattedColumnName: true,
    headerComponent: 'MdsColumnHeader',
    headerComponentParams: {
      onClick: () => {
        args.onLeafClick(node, args.tupleFor(node));
      }
    }
  };
};

/**
 * Renders one column group's headers, nesting them by organize-by level.
 *
 * A leaf's formatter and value-getter index are keyed off the TUPLE that produced it, not off its
 * position in a walk of the tree. Both index into arrays the service returns in tuple order, and
 * while today's SQL orders rows so that a depth-first walk happens to agree, a column silently
 * showing another column's numbers is not a failure worth leaving to that coincidence.
 */
const renderColumnGroupChildren = ({
  segments,
  source,
  emptyPivotColHeader,
  tableFormatter,
  metadataColumnBase,
  valueGetterBase,
  onLeafClick
}: {
  segments: ColumnValue[];
  source: IColumnDataSource | MultiHierarchyColumnDataSource;
  emptyPivotColHeader: string;
  tableFormatter: TableFormatter;
  metadataColumnBase: number;
  valueGetterBase: number;
  onLeafClick: (node: ColumnHeaderNode, tuple: ColumnValue | undefined) => void;
}): HeaderColDef[] => {
  const tupleIndexByPathKey = new Map(
    segments.map((tuple, tupleIndex) => [buildColumnPathKey(columnPathIdsOf(tuple)), tupleIndex])
  );
  const tupleIndexOf = (node: ColumnHeaderNode): number => tupleIndexByPathKey.get(node.pathKey) ?? 0;
  const tupleFor = (node: ColumnHeaderNode): ColumnValue | undefined => {
    const tupleIndex = tupleIndexByPathKey.get(node.pathKey);
    return tupleIndex === undefined ? undefined : segments[tupleIndex];
  };

  return buildColumnHeaderTree(segments).map((node) =>
    createHeaderColDef({
      node,
      source,
      parentId: source.id,
      emptyPivotColHeader,
      leafCount: segments.length,
      formatterFor: (leaf) => tableFormatter(metadataColumnBase + tupleIndexOf(leaf), source) as IValueFormatter,
      valueGetterIndexFor: (leaf) => valueGetterBase + tupleIndexOf(leaf),
      tupleFor,
      onLeafClick
    })
  );
};

const indexBrokenCalcColumns = (sources: IColumnSource[], pivotColumns: ColumnValue[]): Map<string, BrokenCalcColumn> =>
  new Map(findBrokenCalcColumns({ columnGroups: sources, pivotColumns }).map((broken) => [broken.id, broken]));

/**
 * A calc column whose formula references a column absent from this report renders blank — not "–",
 * which reads as an ordinary missing value. The service sends null for it (IRD-1562); the header's
 * warning says why. CSV export reuses this formatter, so the export is blank too.
 *
 * Only a null is blanked: the service decides what it could compute, so a value it did send is
 * always shown, even if this client-side check disagrees.
 */
const calcColumnFormatter = (
  brokenCalcColumn: BrokenCalcColumn | undefined,
  formatter: ReturnType<TableFormatter>
): IValueFormatter => {
  if (!brokenCalcColumn) {
    return formatter as IValueFormatter;
  }
  return ((params: ValueFormatterParams) => (params.value == null ? '' : formatter(params))) as IValueFormatter;
};

export const transformColsToAgGrid = (
  pivotColumns: ColumnValue[],
  sources: IColumnSource[],
  tableFormatter: TableFormatter,
  emptyPivotColHeader: string,
  lensById: Record<string, IReportDatasetLens>,
  unique: string,
  context: QueryContext,
  onClick?: (args: {
    columnIndex: number;
    lens?: IReportDatasetLens;
    /**
     * The clicked column's raw response tuple. Consumers read it positionally (`[1]` the level-1 id,
     * `[2]` its label), so it stays the tuple; `columnPath`/`level` carry the depth-aware identity.
     */
    segment?: ColumnValueSegment[];
    /** The clicked column's level path, outermost first. */
    columnPath?: ColumnValueSegment[];
    /** The clicked column's depth, so a rename is written under the level it belongs to. */
    level?: number;
    source?: IColumnDataSource | MultiHierarchyColumnDataSource;
  }) => unknown,
  showHiddenColumnGroupsIndicator?: boolean,
  asOfDate?: string
) => {
  const columnSegmentsBySource = groupBy(pivotColumns, (val) => val[0]);
  const brokenCalcColumnById = indexBrokenCalcColumns(sources, pivotColumns);

  let valueGetterIndex = 0;
  let metadataColumnIndex = 1;

  return sources
    .map((source, index) => {
      if (isColumnDataSource(source) || isMultiHierarchyColumnDataSource(source)) {
        const name = (source.grouping[0] as IStringConstantExpression).constant;
        const segments = columnSegmentsBySource[source.id];

        if (segments === undefined) return undefined;

        const lens = lensById[source.lens.id];

        const { dateFilter, relativeFilter, rollingNumRangeOption } = source;
        // MH COA filters are a different concept from standard column filters; MdsColumnHeader expects the latter
        const filters = isMultiHierarchyColumnDataSource(source) ? [] : source.filters;
        const rolling = isMultiHierarchyColumnDataSource(source)
          ? (source.relativeRolling ?? rollingNumRangeOption?.value)
          : rollingNumRangeOption?.value;

        // Both counters run across every column group, so this group takes the current values and
        // advances them by its own leaf count before its children read from the reserved block.
        const metadataColumnBase = metadataColumnIndex;
        const valueGetterBase = valueGetterIndex;
        metadataColumnIndex += segments.length;
        valueGetterIndex += segments.length;

        return {
          unique,
          colId: `grouping_${String(index)}`,
          field: 'column_grouping_0',
          headerName: name,
          headerGroupComponent: 'MdsColumnHeader',
          headerGroupComponentParams: {
            text: context.localeText.columnEditorSection,
            lens,
            dateFilter,
            relativeFilter,
            filters,
            rolling,
            asOfDate,
            entityFiscalYearStartMonth: context.entityFiscalYearStartMonth,
            onClick: () => onClick?.({ columnIndex: index, lens }),
            showHiddenColumnGroupsIndicator
          },
          children: renderColumnGroupChildren({
            segments,
            source,
            emptyPivotColHeader,
            tableFormatter,
            metadataColumnBase,
            valueGetterBase,
            onLeafClick: (node, tuple) =>
              onClick?.({
                columnIndex: index,
                lens,
                // The tuple, for the consumers that read it positionally; the path and level beside
                // it, so a rename can be written at the depth the column actually sits at.
                segment: tuple ?? node.ids,
                columnPath: node.ids,
                level: node.level,
                source
              })
          })
        };
      } else if (isCalculationExpression(source) || isMultiHierarchyCalculatedColumnGroup(source)) {
        const brokenCalcColumn = brokenCalcColumnById.get(source.id);
        return {
          unique,
          colId: `grouping_${String(index)}`,
          field: 'column_grouping_0',
          headerName: CALCULATION_COLUMN_GROUP_HEADER,
          headerGroupComponent: 'MdsColumnHeader',
          headerGroupComponentParams: {
            onClick: () => onClick?.({ columnIndex: index }),
            isCalculated: isMultiHierarchyCalculatedColumnGroup(source)
          },
          children: [
            valueColumn({
              colDef: {
                headerName: source.alias,
                field: `${source.id}.`,
                minWidth: AG_GRID_MIN_COLUMN_WIDTH,
                headerComponent: 'MdsColumnHeader',
                headerComponentParams: { onClick: () => onClick?.({ columnIndex: index }), brokenCalcColumn },
                cellClass: CALCULATION_CELL_CLASS
              },
              columnSource: sources[index],
              formatter: calcColumnFormatter(brokenCalcColumn, tableFormatter(metadataColumnIndex++, source)),
              valueGetterIndex: valueGetterIndex++,
              value: source.alias,
              formattedValue: source.alias
            })
          ]
        };
      } else {
        throw new Error('Unsupported column group type');
      }
    })
    .filter(isTruthy);
};

export const transformColsToAgGridForSingleHeader = (
  pivotColumns: ColumnValue[],
  sources: IColumnSource[],
  tableFormatter: TableFormatter,
  emptyPivotColHeader: string,
  lensById: Record<string, IReportDatasetLens>,
  unique: string,
  context: QueryContext,
  onClick?: (args: { columnIndex: number; lens?: IReportDatasetLens }) => unknown
) => {
  const columnSegmentsBySource = groupBy(pivotColumns, (val) => val[0]);
  const brokenCalcColumnById = indexBrokenCalcColumns(sources, pivotColumns);

  let valueGetterIndex = 0;
  let metadataColumnIndex = 1;

  return sources
    .map((source, index) => {
      if (isColumnDataSource(source) || isMultiHierarchyColumnDataSource(source)) {
        const segments = columnSegmentsBySource[source.id];

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!segments) return undefined;

        const lens = lensById[source.lens.id];

        return segments.map((val) => {
          const columnPath = columnPathIdsOf(val);
          const level = Math.max(columnValueLevelCount(val), 1);
          const leafId = columnPath[columnPath.length - 1];
          const leafLabel = val[labelSlot(level)] ?? '';

          return {
            ...createPivotColForSingleHeader({
              emptyPivotColHeader,
              formatter: tableFormatter(metadataColumnIndex++, source) as IValueFormatter,
              valueGetterIndex: valueGetterIndex++,
              segments: segments.length,
              parentId: source.id,
              source,
              value: String(leafId),
              formattedValue: String(leafLabel),
              columnPath,
              level
            }),
            headerClass: AG_MDS_COLUMN_HEADER_CLASS,
            unique,
            isFormattedColumnName: true,
            headerGroupComponent: 'MdsColumnHeader',
            headerGroupComponentParams: {
              entityFiscalYearStartMonth: context.entityFiscalYearStartMonth,
              entitySubdomain: context.entitySubdomain,
              dateFilter: source.dateFilter as ISingleValueColumnFilter<IColumnGroupColumn>[],
              // MH COA filters are a different concept from standard column filters; MdsColumnHeader expects the latter
              filters: isMultiHierarchyColumnDataSource(source) ? [] : source.filters,
              relativeFilter: source.relativeFilter as RelativeFilter,
              lens,
              text: context.localeText.columnEditorSection,
              onClick: () => onClick?.({ columnIndex: index, lens })
            }
          };
        });
      } else if (isCalculationExpression(source) || isMultiHierarchyCalculatedColumnGroup(source)) {
        const brokenCalcColumn = brokenCalcColumnById.get(source.id);
        return {
          ...valueColumn({
            colDef: {
              headerName: source.alias,
              field: `${source.id}.`,
              minWidth: AG_GRID_MIN_COLUMN_WIDTH,
              cellClass: CALCULATION_CELL_CLASS
            },
            columnSource: sources[index],
            formatter: calcColumnFormatter(brokenCalcColumn, tableFormatter(metadataColumnIndex++, source)),
            valueGetterIndex: valueGetterIndex++,
            value: source.alias,
            formattedValue: source.alias
          }),
          headerClass: AG_MDS_COLUMN_HEADER_CLASS,
          headerGroupComponent: 'MdsColumnHeader',
          headerGroupComponentParams: {
            onClick: () => onClick?.({ columnIndex: index }),
            isCalculated: isMultiHierarchyCalculatedColumnGroup(source),
            brokenCalcColumn
          },
          // This is a leaf, and AG Grid reads `headerGroupComponentParams` only from a group, so the
          // header's warning icon does not mount on this path. AG Grid's own header tooltip does.
          ...(brokenCalcColumn ? { headerTooltip: brokenCalcColumnTooltip(brokenCalcColumn) } : {}),
          unique
        };
      } else {
        throw new Error(`Unsupported column group type. Column group: ${JSON.stringify(source)}`);
      }
    })
    .flat()
    .filter(isTruthy);
};

interface GetColumnDataOptions {
  onColumnClick(args: {
    columnIndex: number;
    lens?: IReportDatasetLens;
    segment?: ColumnValueSegment[];
    source?: IColumnDataSource | MultiHierarchyColumnDataSource;
  }): unknown;
}

export async function getColumnData(
  context: QueryContext & {
    formatter: ILocaleFormatter;
    isEditing: boolean;
    /** Start the dataset-description lookup together with the column headers instead of after them. */
    fetchLensesInParallel?: boolean;
  },
  report: IAccountSummary | MultiHierarchyAccountSummary,
  reportConfigurationId: string,
  options: Partial<GetColumnDataOptions> = {}
) {
  const { localeText, formatter, entitySubdomain, entityFiscalYearStartMonth, isEditing } = context;
  const accessToken = await getAccessToken();
  const lensesLookup = context.fetchLensesInParallel ? getLensesForReport(report, reportConfigurationId) : null;
  // A failure is surfaced where the lookup is awaited below; this only keeps it from being reported
  // as unhandled while the column headers are still loading.
  lensesLookup?.catch(() => undefined);
  const result = await api.pivotCols(
    {
      report,
      reportConfigurationId,
      entitySubdomain,
      entityFiscalYearStartMonth,
      entityGroupId: context.entityGroupId
    },
    { cache: context.gridCacheEnabled, accessToken }
  );
  const columnValues = result.data;
  const lensesById = await (lensesLookup ?? getLensesForReport(report, reportConfigurationId));
  const { rowGroups, columnGroups } = report ?? {};
  const { calculatedText, emptyValues } = localeText;
  // Hide Column Group header means it is not showing in view mode
  const allHeadersHidden = areAllColumnGroupHeadersHidden(columnGroups);
  const hideHeaders = !isEditing && (report.hideColumnGroupHeaders || allHeadersHidden);
  const showHiddenIndicator = isEditing && (report.hideColumnGroupHeaders || allHeadersHidden);
  const agGridColumns = hideHeaders
    ? transformColsToAgGridForSingleHeader(
        columnValues,
        columnGroups,
        tableFormatter({ calculatedText, rowGroups, formatter }),
        emptyValues,
        lensesById,
        uniqueId('hack'), // causes ag-grid to detect change in column definitions
        context,
        options.onColumnClick
      )
    : transformColsToAgGrid(
        columnValues,
        columnGroups,
        tableFormatter({ calculatedText, rowGroups, formatter }),
        emptyValues,
        lensesById,
        uniqueId('hack'), // causes ag-grid to detect change in column definitions
        context,
        options.onColumnClick,
        showHiddenIndicator,
        report.asOfDate
      );

  return { agGridColumns, columnValues };
}

export function buildDeletedColumnGroupPlaceholder({
  columnGroups,
  onColumnClick
}: {
  columnGroups: IColumnSource[];
  onColumnClick: (columnIndex: number) => void;
}) {
  const agGridColumns = columnGroups.filter(isMultiHierarchyColumnDataSource).map((group, index) => {
    const name = (group.grouping[0] as IStringConstantExpression).constant;
    return {
      colId: `deleted_grouping_${index}`,
      headerName: name,
      headerGroupComponent: 'MdsColumnHeader',
      headerGroupComponentParams: {
        onClick: () => {
          onColumnClick(index);
        }
      },
      children: [
        {
          colId: `deleted_grouping_${index}_placeholder`,
          headerName: '',
          headerComponent: 'DeletedGroupColumnHeader',
          minWidth: 250,
          flex: 1,
          suppressMovable: true,
          sortable: false,
          filter: false
        }
      ]
    };
  });

  return { agGridColumns, pivotColumns: [] as ColumnValueSegment[][] };
}
